import { Stack, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material/styles"
import { useLayoutEffect, useRef, useState } from "react"
import { lineClampCollapsedSx, toggleButtonSx } from "./style"

type ExpandableCommentTextProps = {
    text: string
    sx?: SxProps<Theme>
}

/** Текст комментария/ответа: до 3 строк, при переполнении — «Подробнее» / «Свернуть». */
export const ExpandableCommentText = ({ text, sx }: ExpandableCommentTextProps) => {
    const [expanded, setExpanded] = useState(false)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        setExpanded(false)
    }, [text])

    useLayoutEffect(() => {
        const element = textRef.current
        if (!element) return

        const updateOverflow = () => {
            const node = textRef.current
            if (!node || text.trim() === "") {
                setIsOverflowing(false)
                return
            }
            if (expanded) {
                return
            }
            setIsOverflowing(node.scrollHeight > node.clientHeight + 1)
        }

        const observer = new ResizeObserver(() => {
            updateOverflow()
        })

        observer.observe(element)
        queueMicrotask(updateOverflow)

        return () => observer.disconnect()
    }, [text, expanded])

    const showToggle = isOverflowing

    return (
        <Stack alignItems="flex-start" width="100%">
            <Typography
                ref={textRef}
                component="div"
                variant="black-14-normal"
                sx={[
                    { whiteSpace: "pre-line", overflowWrap: "anywhere" },
                    !expanded ? lineClampCollapsedSx : null,
                    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
                ]}
            >
                {text}
            </Typography>
            {showToggle && (
                <Typography
                    component="button"
                    type="button"
                    variant="pictonBlue-12-normal"
                    sx={toggleButtonSx}
                    aria-expanded={expanded}
                    onClick={() => setExpanded((v) => !v)}
                >
                    {expanded ? "Свернуть" : "Подробнее"}
                </Typography>
            )}
        </Stack>
    )
}
